import { useState } from "react";
import html2canvas from "html2canvas";
import { Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

/**
 * Iter 253r · Screenshot → Ticket floating action button.
 *
 * Captures the current DOM via html2canvas, opens a dialog letting the
 * user annotate + set priority, and creates a helpdesk ticket. The
 * screenshot is stored inline (data URL) in `description_html` — no
 * separate upload step needed.
 */
export default function ScreenshotReporter() {
  const { activeWorkspaceId, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [img, setImg] = useState(null);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  const [busy, setBusy] = useState(false);

  const capture = async () => {
    setCapturing(true);
    try {
      // Wait a beat so the dialog isn't itself captured on next open.
      await new Promise((r) => setTimeout(r, 200));
      const canvas = await html2canvas(document.body, {
        useCORS: true, allowTaint: true, logging: false,
        backgroundColor: "#ffffff",
        scale: Math.min(1, window.devicePixelRatio || 1),
        ignoreElements: (el) => el.hasAttribute("data-testid")
          && el.getAttribute("data-testid") === "screenshot-reporter-fab",
      });
      const url = canvas.toDataURL("image/png", 0.85);
      setImg(url);
      setSubject(`Feedback from ${document.title || "app"}`);
      setDescription(`URL: ${window.location.href}\n\n`);
      setOpen(true);
    } catch (e) {
      toast.error("Screenshot capture failed");
      // eslint-disable-next-line no-console
      console.error(e);
    } finally { setCapturing(false); }
  };

  const submit = async () => {
    if (!subject.trim()) { toast.error("Subject required"); return; }
    setBusy(true);
    try {
      // Find a default queue.
      const q = await api.get(`/workspaces/${activeWorkspaceId}/helpdesk/queues`).catch(() => ({ data: [] }));
      const queues = Array.isArray(q.data) ? q.data : (q.data?.items || []);
      const queue_id = queues[0]?.id;
      if (!queue_id) {
        toast.error("No helpdesk queue configured — an admin must create one first");
        setBusy(false);
        return;
      }
      const desc_html = `<p>${description.replace(/\n/g, "<br/>")}</p>`
        + (img ? `<hr/><p><em>Screenshot:</em></p><img src="${img}" alt="screenshot" style="max-width:100%;border:1px solid #ddd" />` : "");
      await api.post(`/workspaces/${activeWorkspaceId}/helpdesk/tickets`, {
        queue_id,
        subject, description_html: desc_html,
        priority,
        requester_email: user?.email || null,
        requester_name: user?.name || null,
        tags: ["screenshot", "in-app-report"],
      });
      toast.success("Ticket submitted");
      setOpen(false); setImg(null); setSubject(""); setDescription(""); setPriority("normal");
    } catch (e) { toast.error(apiErrorMessage(e, "Submit failed")); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={capture}
        disabled={capturing}
        className="fixed bottom-4 left-4 z-[60] w-11 h-11 border-2 border-primary bg-background hover:bg-primary hover:text-primary-foreground flex items-center justify-center shadow-lg disabled:opacity-50"
        data-testid="screenshot-reporter-fab"
        title="Screenshot → new ticket"
        aria-label="Report an issue with a screenshot"
      >
        {capturing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
      </button>
      {open && (
        <Dialog open onOpenChange={(v) => { if (!v) setOpen(false); }}>
          <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto rounded-none" data-testid="screenshot-reporter-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="w-4 h-4" /> Screenshot ticket
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {img && (
                <div className="border border-border relative">
                  <img src={img} alt="Screenshot preview" className="w-full max-h-64 object-contain bg-muted/30" />
                  <button type="button" onClick={() => setImg(null)}
                          className="absolute top-1 right-1 w-6 h-6 bg-background/80 border border-border flex items-center justify-center hover:bg-rose-500 hover:text-white"
                          data-testid="screenshot-remove" aria-label="Remove screenshot">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Subject *</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                       className="rounded-none" data-testid="screenshot-subject" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Describe what happened</Label>
                <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)}
                          className="rounded-none" data-testid="screenshot-description" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Priority</Label>
                <select value={priority} onChange={(e) => setPriority(e.target.value)}
                        className="h-9 px-2 border border-border bg-background text-sm"
                        data-testid="screenshot-priority">
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" className="rounded-none" onClick={() => setOpen(false)} data-testid="screenshot-cancel">Cancel</Button>
              <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="screenshot-submit">
                {busy ? "Submitting…" : "Submit ticket"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
